/**
 * WebLLM Engine Wrapper
 * Handles LLM initialization, model loading, and chat completions
 */
import * as webllm from '@mlc-ai/web-llm';

class LLMEngine {
  constructor() {
    this.engine = null;
    this.isLoading = false;
    this.isReady = false;
    this.currentModel = null;
  }

  /**
   * Check if WebGPU is available in the browser
   * @returns {Promise<boolean>}
   */
  static async checkWebGPU() {
    if (!navigator.gpu) {
      return false;
    }
    try {
      const adapter = await navigator.gpu.requestAdapter();
      return adapter !== null;
    } catch (e) {
      console.error('WebGPU check failed:', e);
      return false;
    }
  }

  /**
   * Initialize the engine with a specific model
   * @param {string} modelId - The model identifier
   * @param {function} onProgress - Progress callback (progress: {text, progress})
   * @returns {Promise<boolean>}
   */
  async init(modelId, onProgress = () => {}) {
    if (this.isLoading) {
      console.warn('Model is already loading');
      return false;
    }

    this.isLoading = true;
    this.isReady = false;

    try {
      // Progress callback wrapper
      const initProgressCallback = (report) => {
        const progressPercent = report.progress ? Math.round(report.progress * 100) : 0;
        onProgress({
          text: report.text || 'Chargement...',
          progress: progressPercent
        });
      };

      // Create or reload engine
      if (this.engine && this.currentModel !== modelId) {
        // Unload previous model
        await this.engine.unload();
      }

      this.engine = await webllm.CreateMLCEngine(modelId, {
        initProgressCallback
      });

      this.currentModel = modelId;
      this.isReady = true;
      this.isLoading = false;

      onProgress({
        text: 'Modèle chargé !',
        progress: 100
      });

      return true;
    } catch (error) {
      console.error('Failed to initialize LLM engine:', error);
      this.isLoading = false;
      this.isReady = false;
      
      onProgress({
        text: `Erreur: ${error.message}`,
        progress: 0
      });

      throw error;
    }
  }

  /**
   * Send a chat message and get a streamed response
   * @param {Array} messages - Array of message objects {role, content}
   * @param {function} onToken - Callback for each token received
   * @returns {Promise<string>} - Full response text
   */
  async chat(messages, onToken = () => {}) {
    if (!this.isReady || !this.engine) {
      throw new Error('LLM engine not ready. Please load a model first.');
    }

    try {
      let fullResponse = '';

      // Use streaming API
      const chunks = await this.engine.chat.completions.create({
        messages,
        temperature: 0.7,
        max_tokens: 2048,
        stream: true,
        stream_options: { include_usage: true }
      });

      for await (const chunk of chunks) {
        const delta = chunk.choices[0]?.delta?.content || '';
        fullResponse += delta;
        onToken(delta, fullResponse);
      }

      return fullResponse;
    } catch (error) {
      console.error('Chat completion error:', error);
      throw error;
    }
  }

  /**
   * Get the engine status
   * @returns {object}
   */
  getStatus() {
    return {
      isLoading: this.isLoading,
      isReady: this.isReady,
      currentModel: this.currentModel
    };
  }

  /**
   * Reset the conversation context
   */
  async resetChat() {
    if (this.engine) {
      await this.engine.resetChat();
    }
  }

  /**
   * Unload the current model
   */
  async unload() {
    if (this.engine) {
      await this.engine.unload();
      this.engine = null;
      this.isReady = false;
      this.currentModel = null;
    }
  }
}

// Export singleton instance
export const llmEngine = new LLMEngine();
export default llmEngine;
